import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { fetchCartItems, saveCartItems, clearCartOnServer } from '../services/api';
import { useAuth } from './AuthContext';

const CartContext = createContext(undefined);

export function CartProvider({ children }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();

  const synchronizeCart = useCallback(async () => {
    try {
      const response = await fetchCartItems();
      setItems(response.items || []);
    } catch (error) {
      console.error('Impossible de charger le panier', error);
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    synchronizeCart();
  }, [synchronizeCart]);

  useEffect(() => {
    synchronizeCart();
  }, [user?._id, synchronizeCart]);

  const persistItems = useCallback((updater) => {
    setItems((prevState) => {
      const nextItems = typeof updater === 'function' ? updater(prevState) : updater;
      saveCartItems(nextItems).catch((error) => {
        console.error('Impossible de sauvegarder le panier', error);
      });
      return nextItems;
    });
  }, []);

  const addItem = useCallback(
    ({ productId, name, size, quantity, price, visual }) => {
      if (!productId || !size || !quantity) return;
      const key = `${productId}-${size}`;
      persistItems((prevState) => {
        const existing = prevState.find((item) => item.key === key);
        if (existing) {
          return prevState.map((item) =>
            item.key === key ? { ...item, quantity: item.quantity + quantity } : item
          );
        }
        return [
          ...prevState,
          {
            key,
            productId,
            name,
            size,
            quantity,
            price: Number(price) || 0,
            visual
          }
        ];
      });
    },
    [persistItems]
  );

  const updateQuantity = useCallback(
    (itemKey, delta) => {
      persistItems((prevState) =>
        prevState
          .map((item) =>
            item.key === itemKey ? { ...item, quantity: Math.max(1, item.quantity + delta) } : item
          )
          .filter((item) => item.quantity > 0)
      );
    },
    [persistItems]
  );

  const removeItem = useCallback(
    (itemKey) => {
      persistItems((prevState) => prevState.filter((item) => item.key !== itemKey));
    },
    [persistItems]
  );

  const clearCart = useCallback(() => {
    persistItems([]);
    clearCartOnServer().catch((error) => console.error('Impossible de vider le panier', error));
  }, [persistItems]);

  const total = useMemo(() => items.reduce((acc, item) => acc + item.price * item.quantity, 0), [items]);
  const count = useMemo(() => items.reduce((acc, item) => acc + item.quantity, 0), [items]);

  return (
    <CartContext.Provider value={{ items, addItem, updateQuantity, removeItem, clearCart, total, count, loading }}>
      {children}
    </CartContext.Provider>
  );
}

export function useCart() {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
}
